import { useNavigate } from 'react-router-dom'
import { Row, Col } from 'react-bootstrap'
import { IDealerChangedEvent, IDropDownValue, IPageProps, IRootState } from 'types'
import { DefaultButton, FormDropDownList, FormInput, FormSwitch, FormTextArea, PageHeader, ReturnButton } from 'components'
import DealerSelect from 'components/Dealers/DealerSelect'
import { useRef, useState } from 'react'
import { FormElement, Field, Form, FormClassComponent } from '@progress/kendo-react-form'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'state/store'
import { V1ApiIntegrationsClient, V1ApiDealersClient, V1ApiCasesClient } from 'core/http-common'
import toast from 'core/toast'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { CreateTwilioHostedNumberDto, CreateTwilioTollFreeNumberDto } from 'generated/v1/v1IntegrationsApi'
import { CreateSmsSetupCaseDto, SmsAccountTypeEnum } from 'generated/v1/v1CasesApi'
import { RequiredValidator } from 'core/Validators'
import { DealerDto } from '@v1CasesApi'
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'

const SMSAddPage = ({ title }: IPageProps) => {
    const formRef = useRef<FormClassComponent>(null)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const loading = useSelector((state: IRootState) => state.app.loading)

    const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(undefined)
    const [previousTypeId, setPreviousTypeId] = useState<number | undefined>(undefined)
    const [selectedDealer, setSelectedDealer] = useState<DealerDto | undefined>(undefined)
    const [dealerLoading, setDealerLoading] = useState(false)

    const typeOptions: IDropDownValue[] = [
        { id: 1, text: 'Use Dealership Phone Number' },
        { id: 2, text: 'Get Random Toll Free Number' }
    ]

    const getDefaultWelcomeMessage = (dealerName?: string) => {
        const name = dealerName || 'us'
        return `Thank you for contacting ${name}, a representative will contact you shortly. By submitting this message you consent to receive text messages from or on behalf of the dealer at the mobile phone number you provided and understand that you are opting-in to receive text messages from the dealer or their employees. You also consent to receive phone calls from dealer or their employees at the mobile phone number you provided. Message and data rates may apply. Text STOP to stop receiving messages.`
    }

    const handleDealerChange = async (event: IDealerChangedEvent) => {
        setSelectedDealerId(event.dealerId)

        if (event.dealerId && formRef.current) {
            try {
                setDealerLoading(true)
                const dealer = await V1ApiDealersClient.getDealer(event.dealerId)
                if (dealer?.dealerName) {
                    setSelectedDealer(dealer)
                    formRef.current.onChange('welcomeMessage', { value: getDefaultWelcomeMessage(dealer.dealerName) })
                }

                if (dealer?.primaryContactName) {
                    const nameParts = dealer.primaryContactName.split(' ')
                    if (nameParts.length > 0) {
                        formRef.current.valueSetter('primaryContactFirstName', nameParts[0])
                    }
                    if (nameParts.length > 1) {
                        formRef.current.valueSetter('primaryContactLastName', nameParts[1])
                    }
                }
            } catch (e) {
                toast.responseErrors(e)
            }
            finally {
                setDealerLoading(false)
            }
        }
    }

    const handleDealerClear = () => {
        setSelectedDealerId(undefined)
        if (formRef.current) {
            formRef.current.onChange('welcomeMessage', { value: getDefaultWelcomeMessage() })
            formRef.current.valueSetter('primaryContactFirstName', undefined)
            formRef.current.valueSetter('primaryContactLastName', undefined)
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSubmit = async (values: any) => {

        const selectedTypeId = values.type?.id || values.type

        try {
            await dispatch(startLoadingAction())
            if (selectedTypeId === 1) {
                const dto = new CreateTwilioHostedNumberDto({
                    dealerId: selectedDealerId!,
                    phoneNumber: values.phoneNumber,
                    loaEmail: values.loaEmail,
                    extension: values.extension,
                    sendWelcomeMessage: values.sendWelcomeMessage ?? false,
                    welcomeMessage: values.welcomeMessage,
                    expirationHours: values.messageExpiration ? parseInt(values.messageExpiration) : 0,
                    idleMinutes: values.idleTime ? parseInt(values.idleTime) : 0
                })
                const result = await V1ApiIntegrationsClient.createTwilioHostedNumberAccount(dto)
                const accountSid = result.payload?.twilioAccount?.accountSid
                const verificationCode = result.payload?.verificationCode
                const twilioStatus = result.payload?.twilioAccount?.twilioStatus

                // Create SMS setup cases
                try {
                    const caseDto = new CreateSmsSetupCaseDto({
                        dealerId: selectedDealerId!,
                        accountType: SmsAccountTypeEnum.Hosted,
                        twilioStatus: twilioStatus ?? undefined
                    })
                    await V1ApiCasesClient.createSmsSetupCases(caseDto)
                } catch (caseError) {
                    toast.error('SMS account created, but there was an issue creating the setup cases. Please create them manually.')
                }

                const message = encodeURIComponent(`SMS Account created successfully! Verification call has been sent to the dealer. Give them this verification code: ${verificationCode}`)
                navigate(`/integrations/sms/edit/${accountSid}?code=${verificationCode}&message=${message}`)
            } else {
                const dto = new CreateTwilioTollFreeNumberDto({
                    dealerId: selectedDealerId!,
                    primaryContactFirstName: values.primaryContactFirstName,
                    primaryContactLastName: values.primaryContactLastName,
                    sendWelcomeMessage: values.sendWelcomeMessage ?? false,
                    welcomeMessage: values.welcomeMessage,
                    expirationHours: values.messageExpiration ? parseInt(values.messageExpiration) : 0,
                    idleMinutes: values.idleTime ? parseInt(values.idleTime) : 0
                })
                const result = await V1ApiIntegrationsClient.createTwilioTollFreeAccount(dto)
                const accountSid = result.payload?.accountSid

                // Create SMS setup cases
                try {
                    const caseDto = new CreateSmsSetupCaseDto({
                        dealerId: selectedDealerId!,
                        accountType: SmsAccountTypeEnum.TollFree
                    })
                    await V1ApiCasesClient.createSmsSetupCases(caseDto)
                } catch (caseError) {
                    // Show warning but don't block navigation - Twilio account was created successfully
                    toast.error('SMS account created, but there was an issue creating the setup cases. Please create them manually.')
                }

                // Redirect to Regulatory Settings tab to complete compliance
                const message = encodeURIComponent('SMS Account created successfully! Please complete the toll-free compliance form below.')
                navigate(`/integrations/sms/edit/${accountSid}?tab=regulatorySettings&message=${message}`)
            }
        } catch (e) {
            toast.responseErrors(e)
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    const handleTypeChange = (event: DropDownListChangeEvent) => {
        setTimeout(() => {
            if (formRef.current && selectedDealer?.primaryContactName) {
                const nameParts = selectedDealer.primaryContactName.split(' ')
                if (nameParts.length > 0) {
                    formRef.current.onChange('primaryContactFirstName', { value: nameParts[0] })
                }
                if (nameParts.length > 1) {
                    formRef.current.onChange('primaryContactLastName', { value: nameParts[1] })
                }
            }
        })
    }

    return (
        <div id='SMSAddPage'>
            <PageHeader title={title}>
                <ReturnButton redirectUrl='/integrations/sms' />
            </PageHeader>

            <Form
                onSubmit={handleSubmit}
                ref={formRef}
                ignoreModified={true}
                initialValues={{
                    messageExpiration: 0,
                    idleTime: 0,
                    sendWelcomeMessage: false,
                    welcomeMessage: getDefaultWelcomeMessage()
                }}
                render={(formRenderProps) => {
                    const selectedTypeId = formRenderProps.valueGetter('type')?.id || formRenderProps.valueGetter('type')

                    // Reset fields when type changes
                    if (selectedTypeId !== previousTypeId && previousTypeId !== null) {
                        // Clear the conditional fields using valueSetter to avoid triggering validation
                        if (formRef.current) {
                            formRef.current.valueSetter('phoneNumber', undefined)
                            formRef.current.valueSetter('loaEmail', undefined)
                            formRef.current.valueSetter('extension', undefined)
                            formRef.current.valueSetter('primaryContactFirstName', undefined)
                            formRef.current.valueSetter('primaryContactLastName', undefined)
                            formRef.current.valueSetter('verificationNumber', undefined)
                            formRef.current.valueSetter('messageExpiration', 0)
                            formRef.current.valueSetter('idleTime', 0)
                        }
                        setPreviousTypeId(selectedTypeId)
                    } else if (previousTypeId === null && selectedTypeId) {
                        setPreviousTypeId(selectedTypeId)
                    }

                    const isDealership = selectedTypeId === 1

                    return (
                        <FormElement>
                            <div className='bg-light border p-3 mb-3'>
                                <div className='bg-white border p-3'>
                                    <Row>
                                        <Col lg={6} md={6}>
                                            <Field
                                                name='dealer'
                                                label='Dealer:'
                                                component={DealerSelect}
                                                dealerId={selectedDealerId ?? 0}
                                                onDealerChanged={handleDealerChange}
                                                onDealerCleared={handleDealerClear}
                                                required={true}
                                                loading={dealerLoading}
                                            />
                                        </Col>
                                        <Col lg={6} md={6}>
                                            <Field
                                                name='type'
                                                label='Type:'
                                                component={FormDropDownList}
                                                data={typeOptions}
                                                textField='text'
                                                dataItemKey='id'
                                                onChange={handleTypeChange}
                                            />
                                        </Col>


                                        {selectedDealerId && selectedTypeId && isDealership && (
                                            <>
                                                <Col lg={6} md={6}>
                                                    <Field
                                                        name='phoneNumber'
                                                        label='Dealer Phone #:'
                                                        component={FormInput}
                                                        className='form-control'
                                                        required
                                                        validator={RequiredValidator}
                                                        placeholder='555-555-5555'
                                                    />
                                                </Col>
                                                <Col lg={4} md={4}>
                                                    <Field
                                                        name='loaEmail'
                                                        label='Email for LOA:'
                                                        component={FormInput}
                                                        className='form-control'
                                                        required
                                                        validator={RequiredValidator}
                                                        type='email'
                                                    />
                                                </Col>
                                                <Col lg={2} md={2}>
                                                    <Field
                                                        name='extension'
                                                        label='Extension (for Verification):'
                                                        component={FormInput}
                                                        className='form-control'
                                                    />
                                                </Col>
                                            </>
                                        )}
                                        {selectedDealerId && selectedTypeId && !isDealership && (
                                            <>
                                                <Col lg={6} md={6}>
                                                    <Field
                                                        name='primaryContactFirstName'
                                                        label='Primary Contact First Name:'
                                                        component={FormInput}
                                                        className='form-control'
                                                        required
                                                        validator={RequiredValidator}
                                                    />
                                                </Col>
                                                <Col lg={6} md={6}>
                                                    <Field
                                                        name='primaryContactLastName'
                                                        label='Primary Contact Last Name:'
                                                        component={FormInput}
                                                        className='form-control'
                                                        required
                                                        validator={RequiredValidator}
                                                    />
                                                </Col>
                                            </>
                                        )}
                                        {selectedDealerId && selectedTypeId && (
                                            <>
                                                <Col lg={6} md={6}>
                                                    <Field
                                                        name='messageExpiration'
                                                        label='Message Expiration(Hours):'
                                                        component={FormInput}
                                                        className='form-control'
                                                        min={0}
                                                        format='n0'
                                                        type='number'
                                                    />
                                                </Col>
                                                <Col lg={6} md={6}>
                                                    <Field
                                                        name='idleTime'
                                                        label='Idle Time(Minutes):'
                                                        component={FormInput}
                                                        className='form-control'
                                                        min={0}
                                                        format='n0'
                                                        type='number'
                                                    />
                                                </Col>
                                                <Col lg={12}>
                                                    <Field
                                                        name='welcomeMessage'
                                                        label='Welcome Message:'
                                                        component={FormTextArea}
                                                        className='form-control'
                                                        rows={4}
                                                        disabled={true}
                                                    />
                                                </Col>
                                                <Col lg={12}>
                                                    <Field
                                                        name='sendWelcomeMessage'
                                                        label='Send Welcome Message?'
                                                        component={FormSwitch}
                                                        onLabel='Yes'
                                                        offLabel='No'
                                                    />
                                                </Col>
                                            </>
                                        )}

                                    </Row>
                                </div>

                                {selectedDealerId && selectedTypeId === 1 && (
                                    <DefaultButton
                                        type='submit'
                                        variant='primary'
                                        className='btn-lg mt-3'
                                        disabled={!formRenderProps.allowSubmit || loading}
                                    >
                                        Verify
                                    </DefaultButton>
                                )}

                                {selectedDealerId && selectedTypeId === 2 && (
                                    <DefaultButton
                                        type='submit'
                                        variant='primary'
                                        className='btn-lg mt-3'
                                        disabled={!formRenderProps.allowSubmit || loading}
                                    >
                                        Get Phone Number
                                    </DefaultButton>
                                )}
                            </div>
                        </FormElement>
                    )
                }}
            />
        </div>
    )
}
export default SMSAddPage
